import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Action, Scenario, SimulationState } from '../types';
import { batteryScenario } from '../scenarios/batteryScenario';
import { noInternetScenario } from '../scenarios/noInternetScenario';

const SCENARIOS: Record<string, Scenario> = {
  'no-internet': noInternetScenario,
  demo: noInternetScenario,
  battery: batteryScenario,
};

const defaultScenario = noInternetScenario;

function mergeInitialState(scenario: Scenario): SimulationState {
  return {
    isFixed: scenario.initialState.isFixed ?? false,
    ipReleased: scenario.initialState.ipReleased ?? false,
    batteryReportGenerated:
      scenario.initialState.batteryReportGenerated ?? false,
    batteryIssueIdentified:
      scenario.initialState.batteryIssueIdentified ?? false,
    actionLog: scenario.initialState.actionLog ?? [],
    openWindows: scenario.initialState.openWindows ?? [],
    activeWindow:
      scenario.initialState.activeWindow === undefined
        ? null
        : scenario.initialState.activeWindow,
  };
}

export function postActionsToParent(actions: Action[]): void {
  if (typeof window === 'undefined' || !window.parent) return;
  window.parent.postMessage(
    {
      type: 'SIMULATION_ACTIONS',
      actions,
    },
    '*'
  );
}

interface SimulationContextValue {
  scenario: Scenario;
  state: SimulationState;
  openWindow: (appId: string) => string | undefined;
  closeWindow: (windowId: string) => void;
  setActiveWindow: (windowId: string | null) => void;
  bringToFront: (windowId: string) => void;
  getZIndex: (windowId: string) => number;
  logAction: (action: string, metadata?: Record<string, unknown>) => void;
  markIpReleased: () => void;
  markRenewSuccess: () => void;
  markBatteryReportGenerated: () => void;
  markBatteryIssueIdentified: () => void;
}

const SimulationContext = createContext<SimulationContextValue | null>(null);

function resolveScenario(scenarioParam: string | null): Scenario {
  if (
    !scenarioParam ||
    scenarioParam === 'demo' ||
    scenarioParam === 'no-internet'
  ) {
    return noInternetScenario;
  }
  if (SCENARIOS[scenarioParam]) {
    return SCENARIOS[scenarioParam];
  }
  return defaultScenario;
}

const SINGLETON_APPS = new Set([
  'network-settings',
  'this-pc',
  'recycle-bin',
]);

export function SimulationProvider({
  children,
  scenarioParam,
}: {
  children: React.ReactNode;
  scenarioParam?: string | null;
}): React.ReactElement {
  const scenario = useMemo(
    () => resolveScenario(scenarioParam ?? null),
    [scenarioParam]
  );

  const [state, setState] = useState<SimulationState>(() =>
    mergeInitialState(scenario)
  );

  const zCounter = useRef(10);
  const [windowZIndex, setWindowZIndex] = useState<Record<string, number>>({});

  const logAction = useCallback(
    (action: string, metadata?: Record<string, unknown>) => {
      const entry: Action = {
        action,
        timestamp: new Date(),
        metadata,
      };
      setState((prev) => {
        const actionLog = [...prev.actionLog, entry];
        const next = { ...prev, actionLog };
        queueMicrotask(() => postActionsToParent(next.actionLog));
        return next;
      });
    },
    []
  );

  useEffect(() => {
    logAction('simulation_opened', { scenario: scenario.id });
  }, [scenario.id, logAction]);

  const bringToFront = useCallback((windowId: string) => {
    zCounter.current += 1;
    const nextZ = zCounter.current;
    setWindowZIndex((prev) => ({ ...prev, [windowId]: nextZ }));
    setState((prev) => ({ ...prev, activeWindow: windowId }));
  }, []);

  const getZIndex = useCallback(
    (windowId: string) => windowZIndex[windowId] ?? 10,
    [windowZIndex]
  );

  const openWindow = useCallback(
    (appId: string): string | undefined => {
      if (!scenario.availableApps.includes(appId)) return undefined;

      if (SINGLETON_APPS.has(appId)) {
        setState((prev) => {
          if (prev.openWindows.includes(appId)) {
            return { ...prev, activeWindow: appId };
          }
          return {
            ...prev,
            openWindows: [...prev.openWindows, appId],
            activeWindow: appId,
          };
        });
        queueMicrotask(() => bringToFront(appId));
        return appId;
      }

      const windowId = `${appId}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 7)}`;
      setState((prev) => ({
        ...prev,
        openWindows: [...prev.openWindows, windowId],
        activeWindow: windowId,
      }));
      queueMicrotask(() => bringToFront(windowId));
      return windowId;
    },
    [bringToFront, scenario.availableApps]
  );

  const closeWindow = useCallback((windowId: string) => {
    setState((prev) => {
      const openWindows = prev.openWindows.filter((id) => id !== windowId);
      const activeWindow =
        prev.activeWindow === windowId
          ? openWindows.length
            ? openWindows[openWindows.length - 1]
            : null
          : prev.activeWindow;
      return { ...prev, openWindows, activeWindow };
    });
    setWindowZIndex((prev) => {
      const next = { ...prev };
      delete next[windowId];
      return next;
    });
  }, []);

  const setActiveWindow = useCallback((windowId: string | null) => {
    setState((prev) => ({ ...prev, activeWindow: windowId }));
  }, []);

  const markIpReleased = useCallback(() => {
    setState((prev) => ({ ...prev, ipReleased: true }));
  }, []);

  const markRenewSuccess = useCallback(() => {
    setState((prev) => (prev.isFixed ? prev : { ...prev, isFixed: true }));
  }, []);

  const markBatteryReportGenerated = useCallback(() => {
    setState((prev) =>
      prev.batteryReportGenerated
        ? prev
        : { ...prev, batteryReportGenerated: true }
    );
  }, []);

  const markBatteryIssueIdentified = useCallback(() => {
    setState((prev) =>
      prev.batteryIssueIdentified
        ? prev
        : { ...prev, batteryIssueIdentified: true }
    );
  }, []);

  const value = useMemo(
    (): SimulationContextValue => ({
      scenario,
      state,
      openWindow,
      closeWindow,
      setActiveWindow,
      bringToFront,
      getZIndex,
      logAction,
      markIpReleased,
      markRenewSuccess,
      markBatteryReportGenerated,
      markBatteryIssueIdentified,
    }),
    [
      scenario,
      state,
      openWindow,
      closeWindow,
      setActiveWindow,
      bringToFront,
      getZIndex,
      logAction,
      markIpReleased,
      markRenewSuccess,
      markBatteryReportGenerated,
      markBatteryIssueIdentified,
    ]
  );

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

export function useSimulation(): SimulationContextValue {
  const ctx = useContext(SimulationContext);
  if (!ctx) {
    throw new Error('useSimulation must be used within SimulationProvider');
  }
  return ctx;
}
